import {startOfToday} from 'date-fns';
import {closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNil} from 'ramda';

import {usePatchCoreExchangeRmaAuthorizationMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {
  getApiDateTimeString,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {DialogActions} from './DialogActions';

const RmaAuthorizationFormSchema = z.object({
  rmaRequestDate: z.date().nullable(),
  rmaResponseDate: z.date(),
  rmaNumber: z.string().min(1),
  deliveryNumber: z.string(),
  orderNumber: z.string(),
  rmaNote: z.string(),
});

type RmaAuthorizationForm = z.infer<typeof RmaAuthorizationFormSchema>;

interface RmaAuthorizationDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  rmaRequestDate: string | Nullish;
  onSaved: VoidFunction;
}

export function RmaAuthorizationDialog(props: RmaAuthorizationDialogProps) {
  const [patchRmaAuthorization, {isLoading}] = usePatchCoreExchangeRmaAuthorizationMutation();

  const handleSubmit = async (values: RmaAuthorizationForm) => {
    await patchRmaAuthorization({
      coreExchangeId: props.coreExchangeId,
      body: {
        rmaResponseDate: getApiDateTimeString(values.rmaResponseDate),
        rmaNumber: values.rmaNumber,
        deliveryNumber: values.deliveryNumber,
        orderNumber: values.orderNumber,
        rmaNote: values.rmaNote,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<RmaAuthorizationForm>
      experimentalZodSchema={RmaAuthorizationFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        rmaRequestDate: isNil(props.rmaRequestDate) ? null : parseDate(props.rmaRequestDate),
        rmaResponseDate: startOfToday(),
        rmaNumber: '',
        deliveryNumber: '',
        orderNumber: '',
        rmaNote: '',
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="date"
            name="rmaRequestDate"
            label={i18n.t('entity.warehouse.labels.coreExchangeRmaRequestDate')}
            isDisabled
            data-testid={suffixTestId('inputs.rmaRequestDate', props)}
          />
          <FormField
            control={control}
            type="date"
            name="rmaResponseDate"
            label={i18n.t('entity.warehouse.labels.coreExchangeRmaResponseDate')}
            isRequired
            data-testid={suffixTestId('inputs.rmaResponseDate', props)}
          />
          <FormField
            control={control}
            type="text"
            name="rmaNumber"
            label={i18n.t('entity.warehouse.labels.coreExchangeRmaNumber')}
            isRequired
            data-testid={suffixTestId('inputs.rmaNumber', props)}
          />
          <FormField
            control={control}
            type="text"
            name="deliveryNumber"
            label={i18n.t('entity.warehouse.labels.coreExchangeDeliveryNumber')}
            data-testid={suffixTestId('inputs.deliveryNumber', props)}
          />
          <FormField
            control={control}
            type="text"
            name="orderNumber"
            label={i18n.t('entity.warehouse.labels.orderNumber')}
            data-testid={suffixTestId('inputs.orderNumber', props)}
          />
          <FormField
            control={control}
            type="textarea"
            name="rmaNote"
            label={i18n.t('entity.warehouse.labels.note')}
            data-testid={suffixTestId('inputs.rmaNote', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
